import { evaluateExpression } from '@framework/engine/schema/expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '@framework/engine/schema/expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '@framework/engine/schema/expressions/evaluate/variables/IEvaluationVariables.ts';
import { ExpressionType } from '@framework/engine/schema/expressions/IExpression.ts';
import { collectExpressionReferences } from '@framework/engine/schema/expressions/references/collectExpressionReferences.ts';
import {
  type IReference,
  type IReferenceObjectValue,
} from '@framework/engine/schema/expressions/references/IReference.ts';
import { type IReferenceExpression } from '@framework/engine/schema/expressions/repository/reference/IReferenceExpression.ts';
import { type IWidgetType } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { FormBuilderMetaFlags } from '@framework/engine/validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { getWidgetName } from '@framework/engine/validator/form-builder/getWidgetName.ts';
import { yup } from '@framework/validation/initYup.ts';
import {
  ExportedMetadataRootKey,
  ExportedMetadataSiblingPathsMetaKey,
} from '../../directives/metadata/IMetadataDirective.ts';
import {
  CompetenceMetaFlag,
  CompetenceSecondaryMetaFlag,
  CompetenceSource,
  CompetenceSourceMetaKey,
} from '../competence/competenceMetaFlags.ts';
import { type IMapWidget, MapGeometryType } from './IMapWidget.ts';
import {
  getIntersectionLayersInfo,
  type IMapIntersectionLayer,
} from './layers/getIntersectionLayersInfo.ts';
import { type IMapLayer } from './layers/MapLayer.ts';
import { sumAreasWithDecimal } from './sumAreasWithDecimal.ts';

/**
 * Campi comuni a tutti gli item di intersezione (catastale e custom).
 * Usato come tipo concreto in `MapIntersectionsTable`.
 */
export interface MapIntersectionBaseItem {
  isManual?: boolean;
  feature?: string;
  area?: number;
  affectedArea: number;
  sourceGeometryIndex?: number | null;
}

/** Schema Yup per un item catastale (campi fissi SEZIONE/NUMERO/COMUNE/FOGLIO). */
export const MapCadastralAreaItemSchema = yup
  .object({
    isManual: yup.boolean().default(false).label('Manuale'),
    castralMunicipality: yup.string().required().label('Comune catastale'),
    cadastralSection: yup.string().notRequired().label('Sezione catastale'),
    cadastralSheet: yup.string().required().label('Foglio catastale'),
    cadastralParticle: yup.string().required().label('Particella catastale'),
    // Area
    feature: yup.string().label('Feature GeoJSON'),
    area: yup.number().label('Area particella'),
    affectedArea: yup.number().required().label('Area interessata'),
    // Competenza
    competence: yup
      .string()
      .notRequired()
      .label('Codice layer vettoriale Competenza'),
    // Riferimento alla geometria sorgente
    sourceGeometryIndex: yup
      .number()
      .notRequired()
      .label('Indice geometria sorgente'),
  })
  .required();

export type MapCadastralAreaItem = yup.InferType<
  typeof MapCadastralAreaItemSchema
>;

/**
 * Geometria di base.
 */
export const MapGeometryItemSchema = yup.object({
  feature: yup.string().required().label('GeoJSON Feature'),
  id: yup.string().required().label('Id'),
  area: yup.number().notRequired().label('Area'),
  geometryType: yup
    .string()
    .oneOfEnum(MapGeometryType)
    .notRequired()
    .label('Tipo geometria'),
  length: yup.number().notRequired().label('Lunghezza (m)'),
  geometryIndex: yup.number().notRequired().label('Indice geometria'),
});

export type MapGeometryItem = yup.InferType<typeof MapGeometryItemSchema>;

export const MapCadastralSuffix = '@cadastral';
export const MapGeometriesAreaSuffix = '@area';
export const MapCadastralAreaSuffix = '@cadastralArea';

/**
 * Suffisso per i campi form delle intersezioni custom (non catastali).
 * Formato: `@intersections_<code>`
 */
export const MapIntersectionsSuffix = '@intersections';

/**
 * Suffisso per il campo form della competenza primaria.
 */
export const MapCompetenceSuffix = '@competence';

/**
 * Suffisso per il campo form delle competenze secondarie.
 */
export const MapSecondaryCompetencesSuffix = '@secondaryCompetences';

/**
 * Suffisso per il campo form con l'hash della configurazione usata
 * nell'ultimo calcolo di intersezioni e competenze (vedi
 * `computeIntersectionsCompetencesConfigHash`).
 */
export const MapCalculationConfigHashSuffix = '@calculationConfigHash';

/**
 * Schema per un singolo item di intersezione custom (non catastale).
 * Gli attributi estratti dalla feature sono in `attributes` come coppie chiave-valore.
 */
export const MapIntersectionItemSchema = yup
  .object({
    isManual: yup.boolean().default(false).label('Manuale'),
    attributes: yup.object().label('Attributi intersezione'),
    feature: yup.string().label('Feature GeoJSON'),
    area: yup.number().label('Area particella'),
    affectedArea: yup.number().required().label('Area interessata'),
    sourceGeometryIndex: yup
      .number()
      .notRequired()
      .label('Indice geometria sorgente'),
  })
  .required();

export type MapIntersectionItem = yup.InferType<
  typeof MapIntersectionItemSchema
>;

/**
 * Deriva dallo schema item custom una variante con `attributes` tipizzato
 * sulle chiavi configurate nel layer.
 *
 * La serializzazione dei valori è guidata dallo schema: `serializeFormValues`
 * itera i campi dichiarati nello shape, non le chiavi presenti nel valore.
 * Senza le chiavi nello shape gli attributi dinamici verrebbero persi a ogni
 * ri-serializzazione dei valori — in salvataggio e in validazione — tornando
 * svuotati nei valori persistiti.
 */
function buildCustomIntersectionItemSchema(attributeKeys: string[]) {
  return MapIntersectionItemSchema.shape({
    attributes: yup
      .object(
        Object.fromEntries(attributeKeys.map(attr => [attr, yup.string()])),
      )
      .label('Attributi intersezione'),
  });
}

/**
 * Restituisce il suffisso del campo form per un layer custom di intersezione.
 * Usa `_` come separatore per evitare conflitto con la dot-notation di RHF.
 */
export function getIntersectionFieldSuffix(code: string): string {
  return `${MapIntersectionsSuffix}_${code}`;
}

/**
 * Suffissi dei campi form (relativi al nome del widget) generati per un layer
 * di intersezione. È la fonte unica del naming, condivisa tra lo schema
 * (`createMapSchema`) e le reference (`getMapWidgetSiblingReferences`), così i
 * due restano sempre allineati.
 * - layer catastale (`cadastralPreset`): `@cadastral` + `@cadastralArea`
 * - layer custom: `@intersections_<code>` + `@intersections_<code>Area`
 */
export interface IMapLayerFieldSuffixes {
  /** Suffisso del campo array con gli item di intersezione. */
  intersectionSuffix: string;
  /** Suffisso del campo calcolato con l'area totale interessata. */
  intersectionAreaSuffix: string;
}

export function getMapLayerIntersectionFieldSuffixes(
  layer: IMapIntersectionLayer,
): IMapLayerFieldSuffixes {
  if (layer.intersection.cadastralPreset) {
    return {
      intersectionSuffix: MapCadastralSuffix,
      intersectionAreaSuffix: MapCadastralAreaSuffix,
    };
  }

  const intersectionSuffix = getIntersectionFieldSuffix(layer.code);
  return {
    intersectionSuffix,
    intersectionAreaSuffix: `${intersectionSuffix}Area`,
  };
}

/** Filtra i layer con competenza abilitata. */
export function getCompetenceLayersInfo(widget: IMapWidget): IMapLayer[] {
  return (widget.layers ?? []).filter(layer => layer.competence?.enabled);
}

/**
 * Indica se il widget ha layer di intersezione o di competenza, cioè se c'è
 * un calcolo di cui tracciare l'hash di configurazione.
 */
export function hasMapCalculationLayers(widget: IMapWidget): boolean {
  return (
    getIntersectionLayersInfo(widget).length > 0 ||
    getCompetenceLayersInfo(widget).length > 0
  );
}

/**
 * Schema Yup per la competenza primaria.
 * Contiene il codice layer vettoriale del vincitore, l'`idCompetenza`
 * risolto dall'API e, se presente, la `denominazione`.
 * Un item viene scritto solo quando la competenza è stata individuata e
 * risolta: `vectorLayerCode` e `idCompetenza` sono quindi obbligatori.
 */
export const MapPrimaryCompetenceSchema = yup
  .object({
    vectorLayerCode: yup.string().required().label('Codice layer vettoriale'),
    idCompetenza: yup.string().required().label('ID competenza'),
    denominazione: yup.string().notRequired().label('Denominazione'),
    affectedArea: yup.number().required().label('Area interessata'),
  })
  .required();

export type MapPrimaryCompetenceItem = yup.InferType<
  typeof MapPrimaryCompetenceSchema
>;

/**
 * Schema Yup per un risultato di competenza secondaria.
 * Contiene il codice layer vettoriale (non risolto).
 */
export const MapSecondaryCompetenceItemSchema = yup
  .object({
    vectorLayerCode: yup.string().required().label('Codice layer vettoriale'),
    affectedArea: yup.number().required().label('Area interessata'),
    isPrevalent: yup
      .boolean()
      .required()
      .default(false)
      .label('Ente prevalente'),
    secondaryCompetenceTypeId: yup
      .string()
      .required()
      .label('Tipo competenza secondaria'),
  })
  .required();

export type MapSecondaryCompetenceItem = yup.InferType<
  typeof MapSecondaryCompetenceItemSchema
>;

/** Somma le aree interessate di tutti gli item di intersezione (usa Decimal.js per precisione). */
export function getComputedTotalAffectedArea(
  items: MapIntersectionBaseItem[] | null | undefined,
) {
  return sumAreasWithDecimal((items ?? []).map(item => item?.affectedArea));
}

interface IntersectionSchemaResult {
  entries: [string, yup.AnySchema][];
  siblingPaths: string[];
}

/**
 * Costruisce lo schema Yup di un campo "area totale" calcolato come somma
 * delle aree degli item di un campo fratello.
 *
 * NB: in questo fork di yup la `.transform()` non viene eseguita quando il
 * valore del campo è `undefined` (caso tipico lato validatore, dove le chiavi
 * calcolate non sono presenti nel payload). Per questo il totale viene
 * applicato anche come `.default()`, che viene sempre valutato per i valori
 * assenti; la `.transform()` copre invece il caso in cui il valore sia già
 * presente (ricalcolo autoritativo lato server).
 */
function buildComputedAreaSchema<TItem>(
  siblingFieldName: string,
  computeTotal: (items: TItem[]) => number,
) {
  return yup
    .number()
    .when([siblingFieldName], ([items], schema) => {
      const total = computeTotal((items ?? []) as TItem[]);
      return schema.default(total).transform(() => total);
    })
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipValidationRules,
      FormBuilderMetaFlags.SkipDefaultValue,
    );
}

/** Costruisce lo schema Yup per il layer di intersezione catastale (array item + area totale computed). */
function buildCadastralLayerSchema(
  fieldName: string,
  layer: IMapIntersectionLayer,
): IntersectionSchemaResult {
  const { intersectionSuffix, intersectionAreaSuffix } =
    getMapLayerIntersectionFieldSuffixes(layer);
  const cadastralFieldName = `${fieldName}${intersectionSuffix}`;
  const cadastralAreaFieldName = `${fieldName}${intersectionAreaSuffix}`;

  return {
    entries: [
      [
        cadastralFieldName,
        yup
          .array(MapCadastralAreaItemSchema)
          .default([])
          .formBuilderFlags(
            FormBuilderMetaFlags.SkipValidationRules,
            FormBuilderMetaFlags.SkipDefaultValue,
          ),
      ],
      [
        cadastralAreaFieldName,
        buildComputedAreaSchema<MapCadastralAreaItem>(
          cadastralFieldName,
          getComputedTotalAffectedArea,
        ),
      ],
    ],
    siblingPaths: [cadastralFieldName, cadastralAreaFieldName],
  };
}

/** Costruisce lo schema Yup per un singolo layer di intersezione custom (array item + area totale computed). */
function buildIntersectionLayerSchema(
  fieldName: string,
  layer: IMapIntersectionLayer,
): IntersectionSchemaResult {
  const { intersectionSuffix, intersectionAreaSuffix } =
    getMapLayerIntersectionFieldSuffixes(layer);
  const intersectionFieldName = `${fieldName}${intersectionSuffix}`;
  const intersectionAreaFieldName = `${fieldName}${intersectionAreaSuffix}`;

  return {
    entries: [
      [
        intersectionFieldName,
        yup
          .array(
            buildCustomIntersectionItemSchema(
              layer.intersection.attributes ?? [],
            ),
          )
          .default([])
          .formBuilderFlags(
            FormBuilderMetaFlags.SkipValidationRules,
            FormBuilderMetaFlags.SkipDefaultValue,
          ),
      ],
      [
        intersectionAreaFieldName,
        buildComputedAreaSchema<MapIntersectionItem>(
          intersectionFieldName,
          getComputedTotalAffectedArea,
        ),
      ],
    ],
    siblingPaths: [intersectionFieldName, intersectionAreaFieldName],
  };
}

/**
 * Costruisce lo schema Yup del campo `@competence` (competenza primaria).
 */
function buildPrimaryCompetenceSchema() {
  const schema = yup
    .array(MapPrimaryCompetenceSchema)
    .default([])
    .meta({
      [CompetenceMetaFlag]: true,
      [CompetenceSourceMetaKey]: CompetenceSource.Map,
    })
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipValidationRules,
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipExportMetadata,
    );
  return schema;
}

/** Type guard: valore oggetto non-array (il valore strutturato del tag MAPPA). */
export function isReferenceObjectValue(
  value: unknown,
): value is IReferenceObjectValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Applica il valore di default della mappa a partire da una reference a un tag
 * semantico di tipo MAPPA.
 *
 * Il valore del tag è un oggetto strutturato con chiavi prefissate `root`
 * (`root`, `root@area`, `root@cadastral`, ...), come prodotto da
 * `getSiblingMetadataValue` in `exportMetadataDirective`. Estraiamo la
 * sotto-chiave `root` (le geometrie) e la impostiamo come default sul campo
 * principale. Aree e intersezioni non vengono ripristinate dal tag ma
 * ricalcolate client-side dalle geometrie.
 */
function applyMapDefaultFromReference(
  schema: yup.AnySchema,
  reference: IReference,
) {
  const expression: IReferenceExpression = {
    type: ExpressionType.Reference,
    value: reference,
  };
  const references = collectExpressionReferences(expression);
  const paths = references.map(ref => mapReferenceToPath(ref));

  return schema.parentWhen(paths, (values, schema: yup.AnySchema) => {
    const variables = createEvaluationVariables(references, values);
    const result: unknown = evaluateExpression(expression, { variables });

    if (!isReferenceObjectValue(result)) return schema;

    const value = result[ExportedMetadataRootKey];
    return value != null ? schema.default(value) : schema;
  });
}

export const createMapSchema: IWidgetType<IMapWidget>['createFormSchema'] = ({
  widget,
}) => {
  const fieldName = getWidgetName(widget);
  const geometriesAreaFieldName = `${fieldName}${MapGeometriesAreaSuffix}`;

  const intersectionResults = getIntersectionLayersInfo(widget).map(
    ({ layer }) => {
      if (layer.intersection.cadastralPreset) {
        return buildCadastralLayerSchema(fieldName, layer);
      }
      return buildIntersectionLayerSchema(fieldName, layer);
    },
  );

  const siblingPaths = [
    geometriesAreaFieldName,
    ...intersectionResults.flatMap(result => result.siblingPaths),
  ];

  const competenceLayers = getCompetenceLayersInfo(widget);
  const primaryCompetenceLayer = competenceLayers.find(
    layer => layer.competence?.role === 'primary',
  );
  const hasPrimaryCompetence = primaryCompetenceLayer != null;

  const hasSecondaryCompetence = competenceLayers.some(
    layer => layer.competence?.role === 'secondary',
  );

  const schema: Record<string, yup.AnySchema> = {
    [fieldName]: yup
      .array(MapGeometryItemSchema)
      .default([])
      .meta({
        [ExportedMetadataSiblingPathsMetaKey]: siblingPaths,
      }),
    [geometriesAreaFieldName]: buildComputedAreaSchema<MapGeometryItem>(
      fieldName,
      items => sumAreasWithDecimal(items.map(item => item.area)),
    ),
    ...Object.fromEntries(
      intersectionResults.flatMap(result => result.entries),
    ),
    // Campo tecnico scritto dal manager a ogni ricalcolo: non riceve regole
    // né default del widget e non viene esportato nei metadati. Presente solo
    // se c'è qualcosa da ricalcolare.
    ...(hasMapCalculationLayers(widget)
      ? {
          [`${fieldName}${MapCalculationConfigHashSuffix}`]: yup
            .string()
            .notRequired()
            .formBuilderFlags(
              FormBuilderMetaFlags.SkipValidationRules,
              FormBuilderMetaFlags.SkipDefaultValue,
              FormBuilderMetaFlags.SkipExportMetadata,
            ),
        }
      : {}),
    ...(hasPrimaryCompetence
      ? {
          [`${fieldName}${MapCompetenceSuffix}`]:
            buildPrimaryCompetenceSchema(),
        }
      : {}),
    ...(hasSecondaryCompetence
      ? {
          [`${fieldName}${MapSecondaryCompetencesSuffix}`]: yup
            .array(MapSecondaryCompetenceItemSchema)
            .default([])
            .meta({
              [CompetenceMetaFlag]: true,
              [CompetenceSecondaryMetaFlag]: true,
              [CompetenceSourceMetaKey]: CompetenceSource.Map,
            })
            .formBuilderFlags(
              FormBuilderMetaFlags.SkipValidationRules,
              FormBuilderMetaFlags.SkipDefaultValue,
              FormBuilderMetaFlags.SkipExportMetadata,
            ),
        }
      : {}),
  };

  // Valore di default della mappa da un tag semantico di tipo MAPPA:
  // impostiamo come default SOLO le geometrie (`<fieldName>` <- `root`). Aree e
  // intersezioni non vengono ripristinate dal tag ma ricalcolate client-side
  // dalle geometrie (l'area è un computed; le intersezioni/competenze via
  // `triggerIntersections` all'init/restore), evitando di trascinare valori
  // potenzialmente disallineati rispetto alle geometrie.
  if (widget.defaultValueReference) {
    schema[fieldName] = applyMapDefaultFromReference(
      schema[fieldName],
      widget.defaultValueReference,
    );
  }

  return schema;
};
